import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { C, EASE, DEFAULT_FIELD, media, useContent } from "./theme";
import { SectionHead, splitHeading } from "./Chrome";
import { playThud } from "./sound";

const lines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const ACCENTS = [C.rose, C.gold, C.blushDeep, C.rich];
const TAG_CLIP = "polygon(22% 0, 78% 0, 100% 11%, 100% 100%, 0 100%, 0 11%)";

function Barcode() {
  return (
    <div className="flex items-end gap-[2px] h-6" aria-hidden>
      {[2, 1, 3, 1, 1, 2, 1, 3, 1, 2, 1, 1, 2, 3, 1, 2].map((w, k) => <span key={k} className="h-full" style={{ width: w, background: C.wine }} />)}
    </div>
  );
}

function Photo({ e }) {
  return (
    <div className="relative w-full aspect-[4/3] overflow-hidden rounded-sm" style={{ background: C.blush }}>
      {e.image ? (
        <img src={media(e.image)} alt={e.event} className="w-full h-full object-cover" style={{ filter: "sepia(0.1)" }} />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: `radial-gradient(circle at 30% 20%, ${C.blushLight}, ${C.blushDeep})` }}>
          <span className="ed-serif italic text-4xl" style={{ color: C.burgundy }}>{e.short}</span>
          <span className="ed-mono text-[8px] uppercase tracking-[0.3em] mt-2" style={{ color: C.muted }}>Photo coming soon</span>
        </div>
      )}
    </div>
  );
}

function Front({ e, i, tag }) {
  return (
    <div className="absolute inset-0 flex flex-col px-5 pb-5" style={{ background: C.warm, backfaceVisibility: "hidden", clipPath: tag ? TAG_CLIP : undefined, borderRadius: tag ? 8 : 16, paddingTop: tag ? 52 : 34 }}>
      {tag ? (
        <span className="absolute top-4 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full" style={{ background: C.burgundy, boxShadow: `0 0 0 4px ${C.gold}` }} />
      ) : (
        <span className="absolute top-3 left-1/2 -translate-x-1/2 w-14 h-2.5 rounded-full" style={{ background: C.burgundy }} />
      )}
      <div className="flex items-center justify-between mb-3">
        <span className="ed-mono text-[8px] uppercase tracking-[0.3em] px-2 py-1 rounded-sm" style={{ background: ACCENTS[i % 4], color: C.warm }}>{tag ? "Checked bag" : "All access"}</span>
        <span className="ed-mono text-[8px] uppercase tracking-[0.25em]" style={{ color: C.muted }}>No. {String(i + 1).padStart(3, "0")}</span>
      </div>
      <Photo e={e} />
      <p className="ed-serif text-3xl xl:text-4xl leading-none mt-4" style={{ color: C.burgundy }}>{e.short}</p>
      <p className="ed-mono text-[9px] uppercase tracking-[0.25em] mt-2" style={{ color: C.gold }}>{e.tier}</p>
      <p className="ed-mono text-[9px] uppercase tracking-[0.2em] mt-1" style={{ color: C.muted }}>{e.date} · {e.place}</p>
      <div className="mt-auto flex items-end justify-between pt-3">
        <Barcode />
        <span className="ed-mono text-[8px] uppercase tracking-[0.25em]" style={{ color: C.rose }}>Tap to scan</span>
      </div>
    </div>
  );
}

function Back({ e, tag }) {
  return (
    <div className="absolute inset-0 flex flex-col px-5 pb-5 overflow-hidden" style={{ background: C.warm, backfaceVisibility: "hidden", transform: "rotateY(180deg)", clipPath: tag ? TAG_CLIP : undefined, borderRadius: tag ? 8 : 16, paddingTop: tag ? 52 : 34 }}>
      <p className="ed-mono text-[8px] uppercase tracking-[0.3em]" style={{ color: C.gold }}>Access granted · {e.date}</p>
      <p className="ed-serif text-2xl leading-tight mt-2" style={{ color: C.wine }}>{e.event}</p>
      <p className="ed-serif italic text-base mt-1" style={{ color: C.rose }}>{e.role}</p>
      <ul className="mt-4 space-y-2 text-[13px] leading-snug" style={{ color: C.rich }}>
        {lines(e.points).slice(0, 4).map((p, k) => <li key={k} className="flex gap-2"><span style={{ color: C.gold }}>—</span><span>{p}</span></li>)}
      </ul>
      <div className="mt-auto flex items-end justify-between gap-4 pt-4" style={{ borderTop: `1px solid ${C.burgundy}1a` }}>
        <div className="flex gap-5">
          {lines(e.numbers).slice(0, 2).map((n, k) => {
            const [v, ...rest] = n.split(" ");
            return (
              <div key={k}>
                <p className="ed-serif text-3xl leading-none" style={{ color: C.burgundy }}>{v}</p>
                <p className="ed-mono text-[8px] uppercase tracking-[0.2em] mt-1" style={{ color: C.muted }}>{rest.join(" ")}</p>
              </div>
            );
          })}
        </div>
        <RotateCcw size={14} style={{ color: C.muted }} />
      </div>
    </div>
  );
}

function Pass({ e, i }) {
  const tag = i % 2 === 0;
  const [flipped, setFlipped] = useState(false);
  const [scanning, setScanning] = useState(false);
  const click = () => {
    if (scanning) return;
    if (flipped) return setFlipped(false);
    setScanning(true);
    playThud();
    setTimeout(() => { setScanning(false); setFlipped(true); }, 750);
  };
  return (
    <motion.div
      initial={{ y: 60, opacity: 0, rotate: 0 }}
      whileInView={{ y: 0, opacity: 1, rotate: tag ? -2 : 2 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
      className="relative flex-shrink-0 snap-center flex flex-col items-center"
    >
      {tag ? (
        <svg viewBox="0 0 60 70" className="w-12 h-14 -mb-6 z-10" aria-hidden><path d="M30 68 C 10 40, 50 30, 30 2" fill="none" stroke={C.goldLight} strokeWidth="2.5" strokeLinecap="round" /></svg>
      ) : (
        <span className="flex flex-col items-center -mb-3 z-10" aria-hidden>
          <span className="w-[10px] h-10" style={{ background: `repeating-linear-gradient(180deg, ${C.blushDeep} 0 6px, ${C.rose} 6px 12px)` }} />
          <span className="w-6 h-4 rounded-sm" style={{ background: C.gold }} />
        </span>
      )}
      <motion.button
        data-testid={`lanyard-${i}`}
        onClick={click}
        whileHover={{ y: -8 }}
        className="relative w-[260px] xl:w-[290px] h-[440px] xl:h-[460px] text-left"
        style={{ perspective: 1400, filter: `drop-shadow(0 24px 30px ${C.wine}88)` }}
      >
        <motion.div animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.9, ease: EASE }} className="relative w-full h-full" style={{ transformStyle: "preserve-3d" }} data-testid={`pass-${i}-${flipped ? "back" : "front"}`}>
          <Front e={e} i={i} tag={tag} />
          <Back e={e} tag={tag} />
        </motion.div>
        <AnimatePresence>
          {scanning && (
            <motion.span initial={{ top: "4%" }} animate={{ top: "96%" }} exit={{ opacity: 0 }} transition={{ duration: 0.7, ease: "linear" }} className="absolute inset-x-2 h-[3px] z-20" style={{ background: C.gold, boxShadow: `0 0 18px 4px ${C.goldLight}` }} />
          )}
        </AnimatePresence>
      </motion.button>
    </motion.div>
  );
}

export function EventsSection() {
  const c = useContent("field");
  const items = (c.items?.length ? c.items : DEFAULT_FIELD).slice(0, 6);
  const [head, ital] = splitHeading(c.heading, "In the room where", "it happens.");
  return (
    <section id="field" data-testid="field-section" className="relative min-h-screen flex flex-col justify-center py-24 md:py-28 overflow-hidden" style={{ background: C.burgundy }}>
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12">
        <SectionHead light num="03" eyebrow={c.eyebrow || "Events · In the field"} heading={head} italic={ital} aside={c.aside || "Luggage tags and backstage passes from fashion week to the UN. Tap one to scan it."} />
        <div className="flex gap-6 xl:gap-8 overflow-x-auto snap-x snap-mandatory pb-6 -mx-6 px-6 md:mx-0 md:px-0 xl:justify-center" data-testid="lanyard-rail">
          {items.map((e, i) => <Pass key={i} e={e} i={i} />)}
        </div>
      </div>
    </section>
  );
}
